import { compact } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useGetAssociatedDocumentsQuery } from '@/modules/documents/ManageDocumentAmendments/graphql/GetAssociatedDocuments.generated';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { WorkflowTracker, WorkflowTrackerKind } from '@/types/schema';

import { useDocumentEntityAssociationUpdatesLazyQuery } from './graphql/DocumentEntityAssociationUpdates.generated';

/**
 * Fetches and processes the list of documents associated with the current document.
 * Used for displaying version history and amendments in the document viewer
 * and manage amendments section. Deduplicates results and maps to required format.
 */
export function useAssociatedDocuments(documentId: string) {
  const { data: associatedDocumentsData, refetch } =
    useGetAssociatedDocumentsQuery({
      variables: { documentID: documentId },
      skip: !documentId,
    });

  // This narrow query excludes summaries so a completed association workflow
  // cannot replace summary data while the user is editing it.
  const [refreshDocumentEntityRelationships] =
    useDocumentEntityAssociationUpdatesLazyQuery({
      fetchPolicy: 'network-only',
    });

  const tracksCurrentDocument = useCallback(
    (tracker: WorkflowTracker) =>
      tracker.documents?.some((document) => document.id === documentId) ??
      false,
    [documentId]
  );

  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentExtraction,
    filter: tracksCurrentDocument,
    onCompleted: () => {
      void refetch();
      void refreshDocumentEntityRelationships({
        variables: { documentID: documentId },
      });
    },
  });

  const associatedDocuments = useMemo(() => {
    const associatedDocuments =
      associatedDocumentsData?.getAssociatedDocuments ?? [];

    return compact(associatedDocuments).map((doc) => ({
      id: doc.id,
      name: doc.name,
      type: doc.type,
    }));
  }, [associatedDocumentsData?.getAssociatedDocuments]);

  return { associatedDocuments };
}
